import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import * as HLBoardActions from './hl-board.actions';
import { TPlani8State } from '../reducers';
import {
  TREQUEST_DAILY_OPL_CAPACITIES_FILTERS,
  TREQUEST_HL_BOARD_LISTING,
  TREQUEST_HL_BOARD_SECTION_WISE_LISTING,
  TREQUEST_OPL_CAPACITIES_INFO,
  TREQUEST_OTL_CAPACITIES_INFO,
  TREQUEST_TNA_BOARD_FILTERS,
} from '@monorepo/types';
import * as HLBoardSelectors from './hl-board.selectors';
import { AppNotificationUtilClass, Plani8DataService } from '@monorepo/services';
import { from, lastValueFrom, of } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class HLBoardFacade {
  constructor(
    private readonly store: Store<TPlani8State>,
    private plani8DataService: Plani8DataService,
    private appNotificationUtilClass: AppNotificationUtilClass,
  ) {}

  fetchHLBoardList(payload: TREQUEST_HL_BOARD_LISTING) {
    this.store.dispatch(new HLBoardActions.GetHLBoardList(payload));
  }

  fetchOTLCapacitiesInfo(payload: TREQUEST_OTL_CAPACITIES_INFO) {
    this.store.dispatch(new HLBoardActions.GetOTLCapacitiesInfo(payload));
  }

  fetchWeeklyOPLCapacitiesInfo(payload: TREQUEST_OPL_CAPACITIES_INFO) {
    this.store.dispatch(new HLBoardActions.GetWeeklyOPLCapacitiesInfo(payload));
  }

  fetchDailyOPLCapacitiesInfo(payload: TREQUEST_OPL_CAPACITIES_INFO) {
    this.store.dispatch(new HLBoardActions.GetDailyOPLCapacitiesInfo(payload));
  }

  fetchDailyOPLFilters(payload: TREQUEST_DAILY_OPL_CAPACITIES_FILTERS) {
    const loading$ = this.appNotificationUtilClass.presentLoading('Loading Filters');

    return from(loading$).pipe(
      map((loader) => {
        return [loader];
      }),
      switchMap(([loader]) => {
        return this.plani8DataService
          .getDailyOPLCapacitiesFilter(payload)
          .pipe(finalize(() => this.appNotificationUtilClass.dismissLoadingV2(loader)));
      }),
    );
  }

  async getHLBoardFilters(payload: TREQUEST_TNA_BOARD_FILTERS) {
    const loading$ = await this.appNotificationUtilClass.presentLoading('Loading Filters', 3000);
    return lastValueFrom(this.plani8DataService.getHLBoardFilters(payload)).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading$);
    });
  }

  async syncHLBoard() {
    return lastValueFrom(this.plani8DataService.syncHLBoard());
  }

  async pollHLBoardStatus() {
    return lastValueFrom(this.plani8DataService.getHLPollStatus()).finally(() => {});
  }

  async fetchSectionWiseListing(payloads: TREQUEST_HL_BOARD_SECTION_WISE_LISTING[]) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);

    try {
      return await Promise.all(
        payloads.map((payload) =>
          lastValueFrom(this.plani8DataService.getHLBoardSectionWiseListing(payload).pipe(catchError(() => of(null)))),
        ),
      );
    } finally {
      dismiss();
    }
  }

  getHLBoardList() {
    return this.store.pipe(select(HLBoardSelectors.getHLBoardList));
  }

  getOTLCapacitiesInfo() {
    return this.store.pipe(select(HLBoardSelectors.getOTLCapacitiesInfo));
  }

  getWeeklyOPLCapacitiesInfo() {
    return this.store.pipe(select(HLBoardSelectors.getWeeklyOPLCapacitiesInfo));
  }

  getDailyOPLCapacitiesInfo() {
    return this.store.pipe(select(HLBoardSelectors.getDailyOPLCapacitiesInfo));
  }
}

export type TGetOTLCapacitiesInfo = ReturnType<HLBoardFacade['getOTLCapacitiesInfo']>;
export type TGetWeeklyOPLCapacitiesInfo = ReturnType<HLBoardFacade['getWeeklyOPLCapacitiesInfo']>;
export type TGetDailyOPLCapacitiesInfo = ReturnType<HLBoardFacade['getDailyOPLCapacitiesInfo']>;
